import { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { SUPPORTED_CURRENCIES } from '@/utils/currencyMapping';

/**
 * CurrencyInput - A combobox that allows selecting from a list or typing a custom currency code
 * 
 * @param {string} value - Current currency value
 * @param {function} onChange - Callback when currency changes
 * @param {string} className - Additional CSS classes
 */
export const CurrencyInput = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || 'USD');
  const [filteredCurrencies, setFilteredCurrencies] = useState(SUPPORTED_CURRENCIES);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sync inputValue with external value prop
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter currencies based on input
  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setInputValue(val);
    
    // Filter the list
    const filtered = SUPPORTED_CURRENCIES.filter(curr => 
      curr.code.includes(val) || 
      curr.name.toLowerCase().includes(val.toLowerCase())
    );
    setFilteredCurrencies(filtered);
    setIsOpen(true);
    
    // Auto-update the value if it's a valid 3-letter code
    if (val.length === 3) {
      onChange(val);
    }
  };

  // Handle selecting from dropdown
  const handleSelect = (currency) => {
    setInputValue(currency.code);
    onChange(currency.code);
    setIsOpen(false);
  };

  // Handle blur - commit the value
  const handleBlur = () => {
    // Uppercase and trim the value
    const finalValue = inputValue.toUpperCase().trim();
    if (finalValue) {
      setInputValue(finalValue);
      onChange(finalValue);
    } else {
      // Reset to previous value or default
      setInputValue(value || 'USD');
    }
  };

  // Handle focus - show dropdown
  const handleFocus = () => {
    setFilteredCurrencies(SUPPORTED_CURRENCIES);
    setIsOpen(true);
    // Select all text for easy replacement
    inputRef.current?.select();
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="USD"
        className="uppercase"
        maxLength={3}
      />
      
      {isOpen && filteredCurrencies.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredCurrencies.map((currency) => (
            <button
              key={currency.code}
              type="button"
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                currency.code === inputValue ? 'bg-primary/10 text-primary' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing before click
                handleSelect(currency);
              }}
            >
              <span className="font-medium">{currency.code}</span>
              <span className="text-muted-foreground text-xs">{currency.name}</span>
            </button>
          ))}
        </div>
      )}
      
      {isOpen && filteredCurrencies.length === 0 && inputValue.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
          Press Enter to use "{inputValue}"
        </div>
      )}
    </div>
  );
};

export default CurrencyInput;
